import {
  CHARACTER_DIRECTIONS, cleanOutfit, isCustomizable, PART_SLOTS, SLOT_LABELS,
  type CharacterDesign, type CharacterOutfit, type PartSlot,
} from '../../shared/characters.ts';
import { drawCharacter } from './character-renderer.ts';
import { el, scheduleFrame } from './dom.ts';

const PREVIEW = 96;
/** L'aperçu tourne sur lui-même : une direction toutes les… */
const TURN_MS = 1400;
/** Couleur proposée à une pièce teintable qu'on commence à porter (sans couleur par défaut). */
const DEFAULT_TINT = '#ffffff';
/** Couleurs tirées par « Au hasard ». */
const RANDOM_TINTS = ['#d9534f', '#e6c850', '#3d5a8a', '#2fa39a', '#6b4f3a', '#7a4a2a', '#2b2b33', '#f0f0f0', '#8e5cc7', '#4f9d4a', '#e8893a', '#c8a27a'];

/**
 * Choix de la tenue d'un personnage à l'apparence personnalisable (un PNJ, un joueur) : une pièce par emplacement
 * parmi celles de l'apparence, et sa couleur si elle est teintable, avec un aperçu animé. Masqué pour une apparence
 * qui n'est pas personnalisable (planche complète, preset à la tenue fixe). Partagé par l'administration et le jeu.
 */
export class OutfitEditor {
  readonly element = el('div', { className: 'outfit-editor', hidden: true });
  private readonly preview = el('canvas', { width: PREVIEW, height: PREVIEW, className: 'outfit-preview' });
  private readonly rows = el('div', { className: 'outfit-rows' });
  private readonly onChange: (outfit: CharacterOutfit) => void;
  private design: CharacterDesign | null = null;
  private outfit: CharacterOutfit = {};
  private animating = false;

  constructor(onChange: (outfit: CharacterOutfit) => void = () => {}) {
    this.onChange = onChange;
    const button = (label: string, title: string, onClick: () => void) => {
      const element = el('button', { type: 'button', className: 'ghost', textContent: label, title });
      element.addEventListener('click', onClick);
      return element;
    };
    this.element.append(
      el('div', { className: 'outfit-stage' }, this.preview,
        el('div', { className: 'outfit-actions' },
          button('Au hasard', 'Une pièce et une couleur au hasard à chaque emplacement', () => this.randomize()),
          button('Par défaut', 'Reprendre la tenue par défaut de l\'apparence', () => this.set(this.design?.outfit ?? {})))),
      this.rows);
  }

  /** Tenue choisie (null si l'apparence n'est pas personnalisable). */
  get value(): CharacterOutfit | null {
    return this.design && isCustomizable(this.design) ? structuredClone(this.outfit) : null;
  }

  /** Apparence portée et tenue de départ (null : la tenue par défaut de l'apparence). */
  show(design: CharacterDesign | null, outfit: CharacterOutfit | null): void {
    this.design = design;
    this.element.hidden = !design || !isCustomizable(design);
    if (!design || !isCustomizable(design)) return;
    this.outfit = cleanOutfit(outfit ?? design.outfit, design.parts);
    this.render();
    this.startPreview();
  }

  private set(outfit: CharacterOutfit): void {
    if (!this.design) return;
    this.outfit = cleanOutfit(outfit, this.design.parts);
    this.render();
    this.onChange(structuredClone(this.outfit));
  }

  private randomize(): void {
    const parts = this.design?.parts ?? [];
    const pick = <T>(values: readonly T[]): T | undefined => values[Math.floor(Math.random() * values.length)];
    const outfit: CharacterOutfit = {};
    for (const slot of PART_SLOTS) {
      const part = pick(parts.filter((candidate) => candidate.slot === slot));
      // Le couvre-chef reste facultatif : une fois sur deux.
      if (!part || (slot === 'hat' && Math.random() < 0.5)) continue;
      outfit[slot] = { part: part.id, color: part.tintable ? (pick(RANDOM_TINTS) ?? DEFAULT_TINT) : null };
    }
    this.set(outfit);
  }

  private render(): void {
    const { design } = this;
    if (!design) return;
    const slots = PART_SLOTS.filter((slot) => design.parts.some((part) => part.slot === slot));
    this.rows.replaceChildren(...(slots.length ? slots.map((slot) => this.row(slot))
      : [el('p', { className: 'hint', textContent: 'Cette apparence n\'a encore aucune pièce (onglet Apparences).' })]));
  }

  private row(slot: PartSlot): HTMLElement {
    const design = this.design!;
    const parts = design.parts.filter((part) => part.slot === slot);
    const piece = this.outfit[slot];
    const worn = parts.find((part) => part.id === piece?.part);
    const select = el('select', {},
      el('option', { value: '', textContent: '— Aucun —' }),
      ...parts.map((part) => el('option', { value: part.id, textContent: part.name, selected: part.id === worn?.id })));
    select.addEventListener('change', () => {
      const part = parts.find((candidate) => candidate.id === select.value);
      if (!part) delete this.outfit[slot];
      else this.outfit[slot] = { part: part.id, color: part.tintable ? (piece?.color ?? design.outfit[slot]?.color ?? DEFAULT_TINT) : null };
      this.set(this.outfit);
    });
    const color = el('input', { type: 'color', value: piece?.color ?? DEFAULT_TINT, title: 'Couleur', hidden: !worn?.tintable });
    color.addEventListener('input', () => {
      const current = this.outfit[slot];
      if (!current) return;
      current.color = color.value;
      this.onChange(structuredClone(this.outfit));
    });
    return el('label', { className: 'outfit-row' }, el('span', { textContent: SLOT_LABELS[slot] }), select, color);
  }

  private startPreview(): void {
    if (this.animating) return;
    this.animating = true;
    const loop = (now: number) => {
      if (this.element.hidden || !this.element.isConnected) {
        this.animating = false;
        return;
      }
      this.drawPreview(now);
      scheduleFrame(loop, 250);
    };
    scheduleFrame(loop);
  }

  private drawPreview(now: number): void {
    const ctx = this.preview.getContext('2d');
    if (!ctx || !this.design) return;
    const dir = CHARACTER_DIRECTIONS[Math.floor(now / TURN_MS) % CHARACTER_DIRECTIONS.length] ?? 'down';
    ctx.clearRect(0, 0, PREVIEW, PREVIEW);
    const size = PREVIEW / 2;
    drawCharacter(ctx, this.design, 'walk', dir, now, (PREVIEW - size) / 2, PREVIEW - size - 4, size, this.outfit);
  }
}
